import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { StreamingAcceleratorTrack } from 'fym-shared/src/music24/streamingSections';
import { TypedMemo } from '../../../../common/types';
import { DeltaPill } from '../../../components/primitives/DeltaPill';
import { changePctOf, formatChangePct, formatMonth, formatStreamingNumber } from '../../streamingFormat';
import { acceleratorMessages as messages } from '../../acceleratorMessages';
import { StreamingBlock } from '../../parts/StreamingBlock';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import shared from '../../Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface AcceleratorTracksTableProps {
  tracks: readonly StreamingAcceleratorTrack[];
  month: string;
}

/** "Tracks in Accelerator" of the newest month (design streaming-sections d1). */
const AcceleratorTracksTableComponent: React.FC<AcceleratorTracksTableProps> = ({ tracks, month }) => {
  const intl = useIntl();
  const rows = useMemo(
    () => [...tracks].sort((left, right) => right.acceleratorStreams - left.acceleratorStreams),
    [tracks]
  );
  return (
    <StreamingBlock
      title={intl.formatMessage(messages.tracksTitle)}
      meta={formatMonth(intl, month)}
      testId="streaming-accelerator-tracks"
    >
      <div className={shared.tableWrap}>
        <table className={shared.table}>
          <thead>
            <tr>
              <th>{intl.formatMessage(messages.trackColumn)}</th>
              <th className={shared.numCell}>{intl.formatMessage(messages.allColumn)}</th>
              <th className={shared.numCell}>{intl.formatMessage(messages.fromAccelerator)}</th>
              <th className={shared.numCell}>{intl.formatMessage(messages.shareColumn)}</th>
              <th className={shared.numCell}>{intl.formatMessage(messages.changeColumn)}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const changePct = changePctOf(row.acceleratorStreams, row.previousAcceleratorStreams);
              const change = formatChangePct(intl, changePct);
              const share =
                row.overallStreams !== null && row.overallStreams > 0
                  ? intl.formatNumber(row.acceleratorStreams / row.overallStreams, {
                      style: 'percent',
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                    })
                  : '—';
              return (
                <tr key={row.externalId}>
                  <td>
                    <b>{row.name}</b>
                  </td>
                  <td className={shared.numCell}>{formatStreamingNumber(intl, row.overallStreams)}</td>
                  <td className={shared.numCell}>{formatStreamingNumber(intl, row.acceleratorStreams)}</td>
                  <td className={shared.numCell}>{share}</td>
                  <td className={shared.numCell}>
                    {change !== null && changePct !== null ? <DeltaPill value={changePct} label={change} /> : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className={shared.footnote}>{intl.formatMessage(messages.tracksNote)}</p>
    </StreamingBlock>
  );
};

export const AcceleratorTracksTable = TypedMemo(AcceleratorTracksTableComponent);
